import { IAction } from "../../../base/common/actions.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { isProxyInvariantError } from "../../../base/common/errors.js";
import { ILocalizedString } from "../../action/common/action.js";
import { CommandService } from "../../commands/common/commandService.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import { SetPropertyCommand } from "./propertyCommands.js";
import { IEqualityComparer, PropertyComparisonService } from "./propertyComparison.js";
import { IValueConverter, ValueConversionService, ValueConverterOptions as ConverterContext } from "./converter.js";
import { isColorPropertyLike, isVectorPropertyLike } from "./types.js";
import { IPropertyAccessorService } from "./accessor.js";
import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage.js";
import { IAttachment } from "../../../base/common/attachment.js";
import { PropertyAccessorService } from "./accessorService.js";
import { URI } from "../../../base/common/uri.js";
import { ContextKeyExpr, ContextKeyExpression, IContextKeyService } from "../../contextkey/common/contextkey.js";
import { ILogService } from "../../log/common/log.js";

// #region Property Service

export const IPropertyService = createDecorator<IPropertyService>("propertyService");

export const enum PropertyScope {
    INSPECTOR,
    AI_QUERY,
}

export enum PropertyViewMode {
    DEFAULT,
    ALL,
};

export interface GetPropertyOptions {
    scope: PropertyScope;
    viewMode: PropertyViewMode;
}

export interface IPropertyService {

    readonly _serviceBrand: undefined;
    readonly propertyChanged: Event<PropertyChangedEvent>;

    getProperties(object: object, options: GetPropertyOptions): Promise<IPropertyCollection | null>;
    findProperty(address: PropertyAddress): Promise<IProperty | null>;
    registerProvider(provider: IPropertyProvider): void;
    unregisterProvider(provider: IPropertyProvider): void;
}

export interface IPropertyStateProvider {
    isHidden(object: object, key: string): boolean;
    setHidden?(object: object, key: string, hidden: boolean): void;
}

export interface IPropertyProvider {
    canHandle(object: object): boolean;
    getProperties(object: Record<string, unknown>, options: GetPropertyOptions): Promise<IPropertyCollection | null>;
    findProperty(address: PropertyAddress): Promise<IProperty | null>;
    createProperty(object: Record<string, unknown>, key: string): IProperty | null;
}




// #region Utils

export namespace PropertyUtils {

    export function getAllObjectProperties(obj: any) {
        const allDescriptors: Record<string, PropertyDescriptor> = {};
        let current = obj;
        while (current && current !== Object.prototype) {
            const descriptors = Object.getOwnPropertyDescriptors(current);
            // Don't overwrite - instance properties take precedence
            Object.keys(descriptors).forEach(key => {
                if (!(key in allDescriptors)) {
                    allDescriptors[key] = descriptors[key];
                }
            });
            current = Object.getPrototypeOf(current);
        }

        return allDescriptors;
    }

    export function findPropertyDescriptor(obj: any, key: string): PropertyDescriptor | null {
        let current = obj;
        while (current && current !== Object.prototype) {
            const descriptor = Object.getOwnPropertyDescriptor(current, key);
            if (descriptor) {
                return descriptor;
            }
            current = Object.getPrototypeOf(current);
        }
        return null;
    }

}



// #region Property

/**
 * A unique address to identify a property within an object.
 */
export interface PropertyAddress {
    objectId: string;
    /**
     * The path to the property within the object, e.g., "transform.position.x".
     * Indexes for arrays can be specified using square brackets, e.g., "materials[0].color".
     */
    propertyPath: string;
}

export function isPropertyAddress(obj: unknown): obj is PropertyAddress {
    if (!obj) return false;
    return typeof obj === "object"
        && 'objectId' in obj && typeof (obj as any).objectId === "string"
        && 'propertyPath' in obj && typeof (obj as any).propertyPath === "string";
}


export type PropertyType = "function" | "string" | "number" | "boolean" | "object" | "color" | "vector";

type SetValueArguments<T> = {
    /**
     * The new value to set for the property.
     */
    newValue: T,
    /**
     * The old value of the property, if known. If provided it will be used for undo
     */
    oldValue?: T,
    /**
     * If true, the change notification will not be sent after setting the value.
     */
    withoutNotify?: boolean,
    /**
     * If true, the change will not be recorded in the undo stack.
     */
    withoutUndo?: boolean,

    /**
     * Optional metadata associated with this value change.
     */
    metadata?: ValueMetadata,

    /**
     * Hint for value conversion, e.g., "skybox" for HDRi textures that need equirectangular mapping.
     * Passed through to accessors/converters to guide how values are processed.
     */
    usageHint?: "skybox" | string,
};

/**
 * Metadata that can be provided when setting a property value.
 */
export interface ValueMetadata {
    /**
     * The source of the value, e.g., "user", "system", "ai", etc.
     */
    source?: string;

    /**
     * The filename associated with the value, if applicable.
     */
    filename?: string;

    /**
     * The URI associated with the value, if applicable.
     */
    uri?: string;

    /**
     * The value as a string, if applicable.
     */
    // IMPORTANT: only primitive types allowed here because it's used for changesets and should be easily serializable and no heavy objects
    value?: null | string | boolean | number
    | { x?: number, y?: number, z?: number, w?: number }
    | [number, number] | [number, number, number]
    | [number, number, number, number];

    /**
     * The target of the value assignment: e.g., "material.diffuseMap", "object.position", etc.
     */
    target?: string;
}

export interface PropertyMetadataExtraData {
    value: Record<string, string | number | boolean | PropertyMetadataExtraData>;
}

/**
 * Metadata for a property, such as min/max values for numbers.
 */
export interface PropertyMetadata {

    /**
     * The unique address of the property. This should provide a full path to the property within its owner object.
     */
    address?: string;

    priority?: number; // higher priority properties are shown first

    description?: string | ILocalizedString;

    docsUrl?: string; // URL to documentation for this property

    /** Indicates that this property value is meant to never change */
    static?: boolean;

    /**
     * Indicates if editing for this property should be disabled
     */
    editable?: boolean;

    /**
     * Indicates that this property can not be read
     */
    readable?: boolean;
    /**
     * Indicates that this property can not be written to
     */
    writeable?: boolean;

    /**
     * Indicates that this property should be hidden from the UI
     */
    hidden?: boolean;

    /** Minimum value for the property (if applicable) */
    min?: number;
    /** Maximum value for the property (if applicable) */
    max?: number;
    /** Step value for the property (if applicable) */
    step?: number;

    colorspace?:
    // The colorspace for color properties supported by the browser's <input type="color">
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/color
    "limited-srgb" | "display-p3"
    ;

    alpha?: boolean; // whether the color includes alpha channel

    /**
     * The accepted types for this property. This can be used to indicate special handling in the UI.   
     * Multiple URI types can be specified separated by commas: e.g., "null, image/*, color/*"
     */
    accept?: "null" | "image/*" | "color/*" | ({} & string); // e.g., MIME types like "image/*"

    /**
     * The default value for the property. This can be given by the user or system. It is set when the property is built.
     */
    defaultValue?: any;

    /**
     * The initial value for the property when first created. This can differ from defaultValue if the property is resettable.
     */
    initialValue?: any;

    /**
     * If the property is an enum, the possible options for the enum.
     */
    enumOptions?: Array<{
        label: string | ILocalizedString;
        description?: string | ILocalizedString;
        value: any, hidden?: boolean,
    }>;

    /**
     * If true a multi-select input field should be used. Requires enumOptions to be set.
     */
    multiple?: boolean;

    /**
     * If true, prefer to not create an undo step when changing this property (e.g. 'material.needsUpdate').
     */
    preferNoUndo?: boolean;

    /**
     * A placeholder string to show when the property value is an object and has no special UI yet
     */
    objectValuePlaceholderString?: string;

    /**
     * Can be used to render extra UI for the property (e.g. for mesh information)
     */
    // extraData?: PropertyMetadataExtraData,


    /**
     * The context key for this property (e.g. material.map). Can be used with `when` to control visibility.
     */
    contextKey?: string;

    /**
     * A context key expression that indicates when this property should be visible
     */
    when?: ContextKeyExpression;

    /**
     * Wether to inset the property in the UI (e.g., for nested properties). It can be used for visual grouping.
     */
    inset?: boolean;

    /**
     * When set the property might be shown in a details view or similar.
     */
    detailsId?: string;

    /**
     * When detailsId is set, this label can be used for the details link/button.
     */
    detailsLabel?: string;

    /**
     * Picker configuration. When provided a picker UI should be shown for the property.
     */
    picker?: {
        id: string,
        prefix: string,
    }

    /**
     * For vector properties, the number of components (e.g., 2 for vec2, 3 for vec3, etc.)
     */
    constrainedProportions?: boolean;
}

export const PropertyMetadata = new class {

    /**
     * Tests whether the given property or metadata accepts the given type(s).  
     * @param obj The property or metadata to test.
     * @param accept The accept type(s) to test for.
     * @returns True if the property/metadata accepts the given type(s), false otherwise. If the property/metadata has no accept field, returns undefined.
     * @example 
     * ```ts
     * // Check if a property accepts image types
     * const acceptsImages = PropertyMetadata.testAccept(myProperty, "image/*");
     * 
     * // Check if a metadata accepts color or image types
     * const acceptsColorOrImage = PropertyMetadata.testAccept(myProperty.metadata, "color/*, image/*");
     * ```
     */
    testAccept(obj: IProperty | Pick<PropertyMetadata, "accept">, accept: string): boolean | undefined {
        if (isPropertyLike(obj)) {
            obj = obj.metadata;
        }
        if (obj.accept === undefined) {
            return false;
        }

        // We loop through each accept type and see if any match
        const testArray = accept.split(",").map(a => a.trim());
        const propertyAccepts = obj.accept.split(",").map(a => a.trim());

        for (const test of testArray) {
            const testPrefix = test.endsWith("/*") ? test.substring(0, test.length - 1) : null;

            for (const accept of propertyAccepts) {
                if (accept === test) {
                    return true;
                }
                // Regex match for wildcard types
                if (accept.endsWith("/*")) {
                    // e.g. "image/*" -> "image/"
                    const prefix = accept.substring(0, accept.length - 1);
                    // check if accept starts with this prefix
                    if (test.startsWith(prefix)) {
                        return true;
                    }
                }
                if (testPrefix) {
                    if (accept.startsWith(testPrefix)) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
}



export interface PropertyChangedEvent {
    property: IProperty;

    /**
     * Indicates whether the property value actually changed.   
     * If undefined, the change may be structural (e.g., metadata change).
     */
    value?: boolean;

    /**
     * The previous value of the property, if known.
     */
    previousValue?: any;
}


export interface IProperty {
    readonly onDidChange: Event<PropertyChangedEvent>;
    notifyChanged(args?: Omit<PropertyChangedEvent, "property">): void;

    readonly key: string;
    readonly type: PropertyType | ({} & string); // e.g., "string", "number", "boolean", "color", etc.
    readonly owner: object; // the object that owns this property
    readonly metadata: PropertyMetadata;

    /**
     * Whether the property is readable
     */
    get editable(): boolean;
    set editable(value: boolean);

    /**
     * Whether the property is readable
     */
    get hidden(): boolean;
    set hidden(value: boolean);

    label: string; // human-readable label

    /**
     * Indicates whether the property value is different from its default value.
     */
    valueNotDefault?(): boolean | undefined;
    resetToDefault?(): Promise<boolean> | undefined;

    get value(): unknown;
    set value(v: any);
    setValue(args: SetValueArguments<any>): Promise<boolean>;
    /** Compares the given value to the current property value.
     * If oldValue is provided, it is used for comparison instead of the current property value.
     */
    equalsValue(value: any, oldValue?: any): boolean;
    cloneValue(): any;
    convertValue<T extends unknown>(usage: ConverterContext): Promise<T | null>;

    getTooltip?: () => string | null;
}

export function isPropertyLike(obj: any): obj is IProperty {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const maybeProperty = obj as IProperty;
    return typeof maybeProperty.key === "string"
        && typeof maybeProperty.type === "string"
        && typeof maybeProperty.editable === "boolean"
        && typeof maybeProperty.hidden === "boolean"
        && 'value' in maybeProperty;
}



export interface IPropertyWithType<T> extends IProperty {
    get value(): T;
    set value(v: T);
    setValue(args: SetValueArguments<T>): Promise<boolean>;
}

export interface IPropertyWithEnumOptions extends IProperty {
    readonly metadata: Required<Pick<PropertyMetadata, "enumOptions">> & PropertyMetadata;
}
export function isPropertyWithEnumOptions(obj: IProperty): obj is IPropertyWithEnumOptions {
    return obj.metadata.enumOptions !== undefined && Array.isArray(obj.metadata.enumOptions);
}



export class Property implements IProperty {

    readonly owner: object;
    readonly key: string;
    readonly type: IProperty["type"];
    readonly metadata: PropertyMetadata;

    get editable(): boolean {
        return this.metadata?.editable !== false;
    }
    set editable(value: boolean) {
        if (this.metadata.editable === value) return;
        this.metadata.editable = value;
        this.notifyChanged();
    }

    get readable(): boolean {
        return this.metadata?.readable !== false;
    }
    get writeable(): boolean {
        return this.metadata?.writeable !== false;
    }

    get hidden() {
        return this.metadata?.hidden === true;
    }
    set hidden(value: boolean) {
        if (this.metadata.hidden === value) return;
        this.metadata.hidden = value;
        this.notifyChanged();
    }

    /** Used to actually read/write object values (e.g. complex objects) */
    accessorService: IPropertyAccessorService;
    equalityComparer: IEqualityComparer | null;
    valueConverter: IValueConverter | null;

    label: string = "";

    constructor(
        owner: object,
        key: string,
        type: string,
        metadata?: Partial<PropertyMetadata> | null,
        accessor: IPropertyAccessorService | null = null,
        converter: IValueConverter | null = null,
        comparer: IEqualityComparer | null = null,
    ) {
        this.owner = owner;
        this.key = key;
        this.label = key;
        this.type = type;

        this.accessorService = accessor || PropertyAccessorService.Main;
        this.equalityComparer = comparer || PropertyComparisonService;
        this.valueConverter = converter || ValueConversionService;

        this.metadata = {
            editable: true,
            ...metadata
        }
        if (this.metadata.initialValue === undefined) {
            try {
                this.metadata.initialValue = this.value;
            }
            catch {
                // Ignore errors reading initial value (e.g., proxy invariant violations)
                this.metadata.initialValue = undefined;
            }
        }

        // try to narrow type
        if (this.type === "object") {
            if (isColorPropertyLike(this)) {
                // @ts-ignore
                this.type = "color";
            }
            // else if(isVectorLike(this)) {
            //     this.type = "vector";
            // }
        }

        if (this.label.endsWith("needle")) {
            this.label = this.label.substring(0, this.label.length - "needle".length);
        }
        while (this.label.length > 0 && this.label.endsWith("_")) {
            this.label = this.label.substring(0, this.label.length - 1);
        }
        while (this.label.length > 0 && this.label.startsWith("_")) {
            this.label = this.label.substring(1);
        }
    }

    get value(): unknown {
        try {
            return this.accessorService.readValue(this);
        }
        catch (err) {
            // Catch Proxy invariant violations that can occur when the owner object
            // is wrapped in a proxy (e.g., Threlte/Svelte reactivity) and the property
            // is non-configurable with an object value
            if (isProxyInvariantError(err)) {
                return undefined;
            }
            throw err;
        }
    }

    set value(v: any) {
        this._validateWriteValue(v);
        v = this._convertValueToType(v);

        const current = (this.owner as any)[this.key];
        if (current === v) return;

        // Delegate to command system for proper undo/redo and change propagation
        const commands = CommandService.get();
        if (commands) {
            commands.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                property: this,
                value: v
            });
        }
    }

    valueNotDefault(): boolean | undefined {
        if (this.metadata?.defaultValue === undefined) {
            return undefined;
        }
        return !this.equalsValue(this.metadata.defaultValue);
    }

    // private waitForWriteCompletion(): Promise<void> {
    //     return this._currentWritePromise?.catch(() => {/** ignore */ }) || Promise.resolve();
    // }
    private _currentWritePromise: Promise<boolean> | null = null;

    async setValue(args: SetValueArguments<any>): Promise<boolean> {
        let v = args.newValue;
        this._validateWriteValue(v);
        v = this._convertValueToType(v);

        if (args.withoutNotify || args.withoutUndo) {
            // Directly set value without notifying change system
            const oldValue = args.oldValue;
            return this._currentWritePromise = this.accessorService.writeValue(this, v, args.metadata, args.usageHint).then(() => {
                if (args.withoutNotify !== true) {
                    this._onDidChange.fire({ property: this, value: v, previousValue: oldValue });
                }
                return true;
            })
        }
        else {
            const commands = CommandService.get();
            if (commands) {
                // The property command will handle notifying change system and invoke onDidChange
                return commands.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                    property: this,
                    value: args.newValue,
                    previousValue: args.oldValue,
                    metadata: args.metadata,
                    usageHint: args.usageHint
                }).then(res => res.success);
            }
        }

        return false;
    }

    equalsValue(input: any, oldValue?: any): boolean {
        // If oldValue is not provided, use current value to compare against the input provided
        oldValue = oldValue === undefined ? this.value : oldValue;
        if (this.equalityComparer) {
            return this.equalityComparer.areEqual(input, oldValue);
        }
        else {
            return input === oldValue;
        }
    }

    cloneValue(): any {
        return this.accessorService.cloneValue(this);
    }

    async convertValue<T extends unknown>(intent: ConverterContext): Promise<T | null> {
        if (!this.valueConverter) {
            return null;
        }

        // await this.waitForWriteCompletion();

        try {
            return this.valueConverter.convert<T>(this.value, intent).catch(err => {
                console.error("Error converting property value:", err);
                return null;
            })
        }
        catch (err) {
            console.error("Error converting property value:", err);
            return null;
        }
    }

    private readonly _onDidChange = new Emitter<PropertyChangedEvent>();
    readonly onDidChange = this._onDidChange.event;

    notifyChanged(args?: Omit<PropertyChangedEvent, "property">): void {
        this._onDidChange.fire({ ...args, property: this, });
    }

    private _validateWriteValue(newValue: unknown): void {
        if (!this.editable) {
            throw new Error(`Property ${this.key} is not editable.`);
        }
        if (this.metadata?.static === true) {
            throw new Error(`Property ${this.key} is static and cannot be changed.`);
        }
        if (!this.writeable) {
            // if the accessor indicates that the property is writeable (e.g. write in place) we allow it
            // const accessor = this.accessorService?.getAccessorWithReadAbility({ property: this });
            // if (accessor?.isWriteable?.(this.owner, this.key) === true) {
            //     return;
            // }
            if (this.accessorService.canWrite(this, newValue) === true) {
                return;
            }
            throw new Error(`Property ${this.key} is not writeable.`);
        }
    }

    private _convertValueToType(v: any): any {
        switch (this.type) {
            case "number":
                return Number(v);
            case "boolean":
                return Boolean(v);
            case "string":
                return String(v);
            default:
                return v;
        }
    }
}


// #region PropertyReader






// #region Collections

/**
 * A collection of property groups
 */
export interface IPropertyCollection extends IDisposable {
    name?: string;
    readonly groups: IPropertyGroup[];
}


/**
 * A group of properties
 */
export interface IPropertyGroup {
    id: string;
    name: string;
    expanded: boolean;
    icon: "object" | "material" | "component" | ({} & string);

    description?: string;
    tooltip?: string;

    readonly categories: string[];
    readonly properties: IProperty[];
    readonly actions: IAction[];
    readonly attachments: IAttachment[];
    readonly onDidChange: Event<{ expanded?: boolean }>;
}


export class PropertyGroup implements IPropertyGroup {

    icon: IPropertyGroup["icon"] = "object";

    private readonly _onDidChange = new Emitter<{ expanded?: boolean }>();
    readonly onDidChange = this._onDidChange.event;

    constructor(
        public readonly id: string,
        public name: string,
        public readonly properties: IProperty[] = [],
        public readonly actions: IAction[] = [],
        public readonly attachments: IAttachment[] = [],
    ) {

    }

    addCategory(...category: string[]): void {
        let changed = false;
        for (const cat of category) {
            if (!this.categories.includes(cat)) {
                this.categories.push(cat);
                changed = true;
            }
        }
        if (changed) this._onDidChange.fire({});
    }

    readonly categories: string[] = [];

    notifyChanged() {
        this._onDidChange.fire({});
    }

    private _expanded: boolean = true;
    get expanded(): boolean {
        return this._expanded;
    }
    set expanded(value: boolean) {
        if (this._expanded !== value) {
            this._expanded = value;
            this._onDidChange.fire({ expanded: value });
        }
    }
}


export class PropertyCollection extends Disposable implements IPropertyCollection {

    static readonly Empty = new PropertyCollection("empty", []);

    name?: string;

    constructor(
        name?: string,
        public readonly groups: IPropertyGroup[] = [],
    ) {
        super();
        this.name = name;
    }

    persistFrom(storage: IStorageService) {
        // Store expanded state in storage
        for (const group of this.groups) {
            if (group.id?.length) {
                const expanded = storage.getBoolean(`properties.${group.id}.expanded`, StorageScope.APPLICATION);
                group.expanded = expanded ?? true;
            }

            this._register(group.onDidChange(({ expanded }) => {
                if (group.id?.length && typeof expanded === "boolean") {
                    storage.store(`properties.${group.id}.expanded`, expanded, StorageScope.APPLICATION, StorageTarget.MACHINE);
                }
            }));
        }
    }

    watch(contextKeyService: IContextKeyService, logger: ILogService): void {

        const propertiesWithWhen = new Array<IProperty>();
        const propertiesWithKeys = new Map<string, IProperty>();
        const keysSet = new Set<string>();
        const contextKeyExpressions = new Array<ContextKeyExpression>();
        const keysCreated = new Set<string>();

        for (const group of this.groups) {
            for (const prop of group.properties) {
                // property.metadata.address
                if (prop.metadata.contextKey) {
                    propertiesWithKeys.set(prop.metadata.contextKey, prop);
                    keysSet.add(prop.metadata.contextKey);
                }
                if (prop.metadata.when) {
                    propertiesWithWhen.push(prop);
                    contextKeyExpressions.push(prop.metadata.when!);
                }
            }
        }

        for (const expr of contextKeyExpressions) {
            const keys = expr.keys();
            for (const keyName of keys) {
                const prop = propertiesWithKeys.get(keyName);
                if (!prop) {
                    logger.warn("PropertyCollection.watch: No property found:", keyName);
                }
                else {
                    if (keysCreated.has(keyName)) {
                        continue;
                    }
                    keysCreated.add(keyName);

                    // const isPotentialHeavy = prop.value && typeof prop.value === "object";
                    // if (isPotentialHeavy) logger.debug("PropertyCollection.watch: Potentially heavy property value for context key:", keyName);
                    // else logger.debug("PropertyCollection.watch: Creating Key for", keyName);

                    const key = contextKeyService.createKey(keyName, prop.value as any);
                    this._register(prop.onDidChange(evt => {
                        if (evt.value !== undefined) {
                            key.set(evt.value as any);
                        }
                    }));
                }
            }
        }

        this.updateWhen(propertiesWithWhen, contextKeyService, logger);
        this._register(contextKeyService.onDidChangeContext((e) => {
            if (e.affectsSome(keysSet)) {
                this.updateWhen(propertiesWithWhen, contextKeyService, logger);
            }
        }));

    }

    private updateWhen(props: IProperty[], contextKeyService: IContextKeyService, logger: ILogService | null) {
        for (const prop of props) {
            const when = prop.metadata.when;
            if (when) {
                const match = contextKeyService.contextMatchesRules(when);
                prop.hidden = !match;
                // logger?.debug(`PropertyCollection.updateWhen`, prop.key);
            }
        }

    }
}



